import React, { useEffect, useState } from 'react';
import { apiFetch } from './api';
import './Auth.css';

const GroupAccessEditor = ({ user, groups, busy, onSave }) => {
  const [selected, setSelected] = useState(user.allowed_groups || ['all_tickers']);
  useEffect(() => setSelected(user.allowed_groups || ['all_tickers']), [user.allowed_groups]);
  if (user.is_admin) return <span className="all-lists-label">All lists</span>;
  const choices = groups.filter((group) => group !== 'watchlist');
  const toggle = (group) => setSelected((current) => current.includes(group)
    ? current.filter((item) => item !== group)
    : [...current, group]);
  return <details className="group-access-editor">
    <summary>Lists ({selected.length})</summary>
    <div className="group-access-popover">
      <div className="group-access-options">
        {choices.map((group) => <label key={group}>
          <input type="checkbox" checked={group === 'all_tickers' || selected.includes(group)} disabled={group === 'all_tickers' || busy} onChange={() => toggle(group)} />
          <span>{group === 'all_tickers' ? 'All' : group}</span>
        </label>)}
      </div>
      <button type="button" disabled={busy} onClick={() => onSave(user.username, selected)}>Save lists</button>
    </div>
  </details>;
};

const UserAdmin = ({ onClose }) => {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newCanTrade, setNewCanTrade] = useState(false);
  const [resetUsername, setResetUsername] = useState('');
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const loadUsers = async () => {
    let lastError;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const response = await apiFetch('/api/admin/users');
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.error || 'Unable to load users');
        const loadedUsers = body.users || [];
        setUsers(loadedUsers);
        setResetUsername((current) => current || loadedUsers[0]?.username || '');
        setError('');
        return;
      } catch (requestError) {
        lastError = requestError;
        if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
    throw lastError;
  };

  useEffect(() => {
    loadUsers().catch((requestError) => setError(requestError.message));
    apiFetch('/api/groups').then((response) => response.json()).then((items) => setGroups(Array.isArray(items) ? items : [])).catch(() => {});
  }, []);

  const run = async (request, successMessage) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await request();
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Request failed');
      setMessage(successMessage);
      await loadUsers();
      return true;
    } catch (requestError) {
      setError(requestError.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const createUser = async (event) => {
    event.preventDefault();
    const username = newUsername.trim().toLowerCase();
    const created = await run(
      () => apiFetch('/api/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password: newPassword, canTrade: newCanTrade }),
      }),
      `${username} created`
    );
    if (created) {
      setNewUsername('');
      setNewPassword('');
      setNewCanTrade(false);
      setResetUsername(username);
    }
  };

  const setTradeAccess = (username, enabled) => run(
    () => apiFetch(`/api/admin/users/${encodeURIComponent(username)}/trade-access`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    }),
    `${username} trade logging ${enabled ? 'enabled' : 'disabled'}`
  );

  const saveGroups = (username, selectedGroups) => run(
    () => apiFetch(`/api/admin/users/${encodeURIComponent(username)}/groups`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ groups: selectedGroups }),
    }),
    `${username}'s screener lists updated`
  );

  const submitPasswordReset = async (event) => {
    event.preventDefault();
    const username = resetUsername.trim().toLowerCase();
    const reset = await run(
      () => apiFetch(`/api/admin/users/${encodeURIComponent(username)}/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: resetPasswordValue }),
      }),
      `${username}'s password reset`
    );
    if (reset) setResetPasswordValue('');
  };

  return (
    <div className="admin-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="admin-dialog" role="dialog" aria-modal="true" aria-label="User administration">
        <header>
          <div><strong>Users</strong><span>Administrator</span></div>
          <button type="button" onClick={onClose} aria-label="Close user administration">×</button>
        </header>

        <form className="create-user-form" onSubmit={createUser}>
          <h2>Create user</h2>
          <input
            placeholder="Username"
            value={newUsername}
            maxLength="40"
            onChange={(event) => setNewUsername(event.target.value.toLowerCase())}
          />
          <input
            type="password"
            placeholder="Temporary password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
          <label className="trade-access-checkbox">
            <input type="checkbox" checked={newCanTrade} onChange={(event) => setNewCanTrade(event.target.checked)} />
            <span>Allow trade logging</span>
          </label>
          <button type="submit" disabled={busy || !newUsername || !newPassword}>Create</button>
        </form>

        <section className="user-access-section">
          <h2>User permissions</h2>
          {users.map((user) => <div className="user-permission-card" key={user.username}>
            <div className="user-access-row">
              <span>{user.username}</span>
              <button type="button" className={user.trading_enabled ? 'enabled' : ''} disabled={busy} onClick={() => setTradeAccess(user.username, !user.trading_enabled)}>
                Trades: {user.trading_enabled ? 'On' : 'Off'}
              </button>
              <GroupAccessEditor user={user} groups={groups} busy={busy} onSave={saveGroups} />
            </div>
          </div>)}
        </section>

        <form onSubmit={submitPasswordReset}>
          <h2>Reset password</h2>
          <select value={resetUsername} onChange={(event) => setResetUsername(event.target.value)}>
            {users.map((user) => <option key={user.username} value={user.username}>{user.username}</option>)}
          </select>
          <input
            type="password"
            placeholder="New password"
            autoComplete="new-password"
            value={resetPasswordValue}
            onChange={(event) => setResetPasswordValue(event.target.value)}
          />
          <button type="submit" disabled={busy || !resetUsername || !resetPasswordValue}>Reset</button>
        </form>

        <p className="password-rule">At least 8 characters with one special character.</p>
        {message && <div className="auth-message">{message}</div>}
        {error && <div className="auth-error">{error}</div>}
      </section>
    </div>
  );
};

export default UserAdmin;

